// CARO. Casa de estilo — URL-based locale (ES at root, EN under /en/..., FR under /fr/...).
//
// The active locale is derived from the URL path, so each language has its own
// canonical URLs (needed for hreflang / SEO). EN/FR also translate the path
// segments (`/fr/boutique/maison`, table in @/lib/seo/routes). React Router
// mounts with a per-locale `basename` and a per-locale route table (routesFor);
// links are written with LOGICAL (Spanish) paths through `lp(...)`, which
// translates them for this load's locale (the basename adds the prefix).
// Switching locale is a full navigation to the other language's URL (basename
// can't change in place), which also lets the edge serve the right
// server-rendered <head>. The prefix logic itself lives in @/lib/seo (shared
// with the Worker).

import type { Locale } from '@/types';
import { DEFAULT_LOCALE } from '@/config/site';
import { localeFromPath, stripLocalePrefix, localizedPath, toPublicPath } from '@/lib/seo';

// stripLocale: logical path without the prefix; withLocale: logical path with it.
export { localeFromPath, stripLocalePrefix as stripLocale, localizedPath as withLocale };

const CURRENT_PATH = typeof window !== 'undefined' ? window.location.pathname : '/';

/** Locale for this page load (from the URL). */
export const INITIAL_LOCALE: Locale = localeFromPath(CURRENT_PATH);

/** React Router basename for this locale ('/en', '/fr'; '/' for es). */
export const ROUTER_BASENAME =
  INITIAL_LOCALE === DEFAULT_LOCALE ? '/' : localizedPath(INITIAL_LOCALE, '/');

/**
 * Router path of a logical path for this load's locale, without the locale
 * prefix (the basename adds it): `lp('/shop/casa')` → `/boutique/maison` under
 * /fr. Every internal link target in public client code goes through it.
 */
export function lp(logicalPath: string): string {
  return toPublicPath(INITIAL_LOCALE, logicalPath);
}

/** URL of the current page in `target` locale (for the language switcher). */
export function localeSwitchUrl(target: Locale): string {
  const { pathname, search, hash } = window.location;
  return `${localizedPath(target, stripLocalePrefix(pathname))}${search}${hash}`;
}
